import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { socketIo } from 'context/socket';

const REPLY_PROGRESS_EVENT = 'ASSISTANT_REPLY_PROGRESS';

/** What the assistant is doing while it uses its tools (ASSISTANT_ACTIVITY_* labels). */
export type AssistantActivity =
    | 'SEARCHING_HELP'
    | 'READING_ARTICLE'
    | 'LOOKING_UP_ORDER'
    | 'CHECKING_ACCOUNT'
    | 'CHECKING_CONNECTION'
    | 'CHECKING_INTEGRATIONS'
    | 'DRAFTING_REPORT';

type ReplyProgress = { streamId: string; sequence: number; text: string; activity?: AssistantActivity };

/**
 * DD-3780: the answer so far, while the assistant writes it. The backend sends it as
 * socket events tagged with the id this hook made for the question; the mutation's
 * response is the real answer and replaces the preview. Without a socket the preview
 * stays empty and the chat still works.
 */
export const useAssistantReplyStream = () => {
    const socket = useContext(socketIo.getSocketContext());
    const [previewText, setPreviewText] = useState('');
    const [activity, setActivity] = useState<AssistantActivity>();
    const currentStream = useRef<{ streamId: string; sequence: number; hasText: boolean } | null>(null);

    useEffect(() => {
        if (!socket) {
            return;
        }
        const handleProgress = ({ data }: { data: ReplyProgress }) => {
            const stream = currentStream.current;
            if (!stream || data.streamId !== stream.streamId || data.sequence <= stream.sequence) {
                return;
            }
            stream.sequence = data.sequence;
            stream.hasText = stream.hasText || !!data.text;
            setPreviewText(data.text);
            setActivity(data.activity);
        };
        socket.on(REPLY_PROGRESS_EVENT, handleProgress);
        return () => {
            socket.off(REPLY_PROGRESS_EVENT, handleProgress);
        };
    }, [socket]);

    const beginStream = useCallback((): string => {
        const streamId = crypto.randomUUID();
        currentStream.current = { streamId, sequence: 0, hasText: false };
        setPreviewText('');
        setActivity(undefined);
        return streamId;
    }, []);

    const endStream = useCallback(() => {
        currentStream.current = null;
        setPreviewText('');
        setActivity(undefined);
    }, []);

    /** Whether answer text arrived while it was written (not only activity updates). */
    const hasStreamedText = useCallback(() => !!currentStream.current?.hasText, []);

    return { previewText, activity, beginStream, endStream, hasStreamedText };
};
